// src/components/pages/Profile.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FaUser, FaEdit, FaMapMarkerAlt, FaShoppingBag, 
  FaTrash, FaExclamationTriangle, FaFilePrescription, 
  FaTicketAlt, FaQuestionCircle, FaShieldAlt, FaDownload,
  FaChevronRight, FaHistory, FaPhone, FaSpinner, FaCheck,
  FaSignOutAlt, FaUserCircle, FaCamera, FaSave, FaTimes
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';
import { getProfile, updateProfile, clearError, setProfileCompleted, updateUserName, logoutUser } from '../../redux/slices/authSlice';

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { user, isLoading, isAuthenticated, error } = useSelector((state) => state.auth);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [profileData, setProfileData] = useState({
    name: '',
    phone: '',
    address: '',
    profile_image: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [phoneError, setPhoneError] = useState('');
  const fileInputRef = useRef(null);

  // ✅ Get user data from Redux or localStorage
  const getUserData = () => {
    if (user && user.name && user.name !== 'User' && user.name !== 'Account') {
      return user;
    }
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser && storedUser.name && storedUser.name !== 'User' && storedUser.name !== 'Account') {
      return storedUser;
    }
    return null;
  };

  const userData = getUserData();

  // ✅ Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token && !isAuthenticated) {
        console.log('⚠️ Profile page: User not authenticated');
        navigate('/login');
        return;
      }
      
      console.log('🔄 Profile page: Fetching profile...');
      try {
        const result = await dispatch(getProfile()).unwrap();
        console.log('✅ Profile fetched successfully:', result);
        setProfileLoaded(true);
        
        if (result?.data || result) {
          const userData = result?.data || result;
          console.log('📦 User data from API:', userData);
          
          setProfileData({
            name: userData.name || userData.full_name || '',
            phone: userData.phone || userData.mobile || '',
            address: userData.address || '',
            profile_image: userData.profile_image || null
          });
          
          if (userData.profile_image) {
            const imageUrl = getImageUrl(userData.profile_image);
            console.log('🖼️ Generated image URL:', imageUrl);
            setPreviewImage(imageUrl);
          }
        }
      } catch (error) {
        console.error('❌ Failed to fetch profile:', error);
      }
    };

    fetchProfile();
  }, [dispatch, isAuthenticated, navigate]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.includes('uploads/')) {
      return `https://admin.thefoodies.in/public/${imagePath}`;
    }
    if (imagePath.startsWith('profile_images/')) {
      return `https://admin.thefoodies.in/public/uploads/${imagePath}`;
    }
    return `https://admin.thefoodies.in/public/uploads/profile_images/${imagePath}`;
  };

  const handleImageError = () => {
    console.error('❌ Image failed to load');
    setImageError(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }
      setProfileData(prev => ({ ...prev, profile_image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileData(prev => ({ ...prev, profile_image: null }));
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ✅ Updated: Only allow numeric input for phone
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // ✅ Only allow digits (0-9)
      const numericValue = value.replace(/\D/g, '');
      setProfileData(prev => ({ ...prev, [name]: numericValue }));
      
      // Validate phone number length
      if (numericValue.length > 0 && numericValue.length < 10) {
        setPhoneError('Phone number must be 10 digits');
      } else if (numericValue.length === 10) {
        setPhoneError('');
      } else if (numericValue.length > 10) {
        setPhoneError('Phone number cannot exceed 10 digits');
        // Truncate to 10 digits
        setProfileData(prev => ({ ...prev, [name]: numericValue.slice(0, 10) }));
      } else {
        setPhoneError('');
      }
    } else {
      setProfileData(prev => ({ ...prev, [name]: value }));
    }
  };

  // ✅ Handle phone input - Block non-numeric characters
  const handlePhoneKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrow keys
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (allowedKeys.includes(e.key)) {
      return;
    }
    // Allow: numbers 0-9
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  // ✅ Handle phone paste - Only allow numbers
  const handlePhonePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const numericOnly = pastedText.replace(/\D/g, '');
    if (numericOnly) {
      const currentValue = profileData.phone || '';
      const newValue = (currentValue + numericOnly).slice(0, 10);
      setProfileData(prev => ({ ...prev, phone: newValue }));
    }
  };

  // ✅ Handle profile update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // ✅ Validate phone number before submitting
    const phone = profileData.phone || '';
    if (phone.length > 0 && phone.length !== 10) {
      setPhoneError('Phone number must be exactly 10 digits');
      return;
    }
    
    setUploading(true);
    setSuccessMessage('');
    dispatch(clearError());
    
    try {
      const formData = new FormData();
      formData.append('name', profileData.name);
      formData.append('phone', profileData.phone);
      formData.append('address', profileData.address || '');
      
      if (profileData.profile_image && typeof profileData.profile_image !== 'string') {
        formData.append('profile_image', profileData.profile_image);
      }
      
      const result = await dispatch(updateProfile(formData)).unwrap();
      console.log('✅ Profile updated:', result);
      
      setSuccessMessage('Profile updated successfully!');
      setIsEditing(false);
      
      // ✅ Fetch fresh profile data
      const profileResult = await dispatch(getProfile()).unwrap();
      console.log('✅ Fresh profile fetched:', profileResult);
      
      const userData = profileResult?.data || profileResult;
      if (userData) {
        const updatedUserData = {
          id: userData.id,
          name: userData.name || profileData.name,
          phone: userData.phone || profileData.phone,
          address: userData.address || profileData.address,
          profile_image: userData.profile_image || profileData.profile_image,
          is_new_user: false
        };
        
        // ✅ Update localStorage
        localStorage.setItem('user', JSON.stringify(updatedUserData));
        localStorage.setItem('profileCompleted', 'true');
        localStorage.setItem('isNewUser', 'false');
        
        // ✅ Update auth login data
        const authUser = JSON.parse(localStorage.getItem('tiffinUserLogin') || '{}');
        authUser.name = updatedUserData.name;
        authUser.profile_completed = true;
        localStorage.setItem('tiffinUserLogin', JSON.stringify(authUser));
        
        console.log('✅ localStorage updated with:', updatedUserData);
        
        // ✅ Update Redux state directly
        dispatch(updateUserName(updatedUserData.name));
        dispatch(setProfileCompleted(true));
        
        // ✅ Dispatch events to notify navbar
        window.dispatchEvent(new CustomEvent('profileUpdated', { 
          detail: { 
            name: updatedUserData.name,
            phone: updatedUserData.phone,
            address: updatedUserData.address 
          } 
        }));
        window.dispatchEvent(new Event('storage'));
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('❌ Failed to update profile:', err);
      alert(typeof err === 'string' ? err : 'Failed to update profile. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setPhoneError('');
    const currentUser = getUserData();
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        phone: currentUser.phone || '',
        address: currentUser.address || '',
        profile_image: currentUser.profile_image || null
      });
      if (currentUser.profile_image) {
        setPreviewImage(getImageUrl(currentUser.profile_image));
      } else {
        setPreviewImage(null);
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayName = userData?.name || 'My Account';
  const displayPhone = userData?.phone || localStorage.getItem('tiffinPhone') || '';
  const displayAddress = userData?.address || '';
  const displayImage = userData?.profile_image || null;
  
  const getDisplayImageUrl = () => {
    if (previewImage) return previewImage;
    if (displayImage && !imageError) {
      return getImageUrl(displayImage);
    }
    return null;
  };

  const displayImageUrl = getDisplayImageUrl();

  if (isLoading && !profileLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl mx-auto" style={{ color: colors.primary }} />
          <p className="mt-4" style={{ color: colors.textSecondary }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  // ✅ Settings - REMOVED: Saved Addresses, My Prescriptions, E-Gift Cards
  const settings = [
    { icon: <FaShoppingBag />, label: 'My Orders', path: '/my-orders' },
    { icon: <FaQuestionCircle />, label: "FAQ's", path: '/faq' },
    { icon: <FaShieldAlt />, label: 'Account Privacy', path: '/privacy' },
  ];

  // ✅ Updated Logout Handler
  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        console.log('📤 Logging out from profile...');
        await dispatch(logoutUser()).unwrap();
        navigate('/');
      } catch (error) {
        console.error('❌ Logout error:', error);
        // Even if API fails, clear local data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('isNewUser');
        localStorage.removeItem('profileCompleted');
        localStorage.removeItem('tiffinPhone');
        localStorage.removeItem('tiffinUserLogin');
        navigate('/');
      }
    }
  };

  return (
    <motion.div 
      className="py-4 max-w-2xl mx-auto px-4 pb-20"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      style={{ backgroundColor: 'transparent' }}
    >
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: colors.text }}>
          My Account
        </h1>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105 flex items-center gap-2"
            style={{ 
              backgroundColor: colors.primary,
              color: '#FFFFFF'
            }}
          >
            <FaEdit /> Edit Profile
          </button>
        )}
      </div>

      {successMessage && (
        <div className="mb-4 p-3 rounded-xl flex items-center gap-2"
             style={{ 
               backgroundColor: '#22C55E20',
               color: '#22C55E',
               border: '1px solid #22C55E'
             }}>
          <FaCheck />
          <span>{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-xl flex items-start gap-2"
             style={{ 
               backgroundColor: '#FEF2F2',
               color: '#DC2626',
               border: '1px solid #FECACA'
             }}>
          <span className="mt-0.5">⚠️</span>
          <span>{error}</span>
          <button
            onClick={() => dispatch(clearError())}
            className="ml-auto text-xs font-medium hover:underline"
            style={{ color: '#DC2626' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Profile Card */}
      <div 
        className="rounded-2xl p-6 mb-6 text-center"
        style={{ 
          background: `linear-gradient(135deg, ${colors.primary}, ${colors.primary}CC)`,
        }}
      >
        <div className="relative w-24 h-24 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3 text-3xl text-white overflow-hidden">
          {displayImageUrl && !imageError ? (
            <img 
              src={displayImageUrl} 
              alt={displayName} 
              className="w-full h-full object-cover"
              onError={handleImageError}
            />
          ) : (
            <FaUserCircle className="w-full h-full" />
          )}
          
          {isEditing && (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 border-2 border-white"
                style={{ backgroundColor: colors.primary }}
              >
                <FaCamera className="text-xs" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              {previewImage && (
                <button
                  onClick={handleRemoveImage}
                  className="absolute bottom-0 left-0 w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 border-2 border-white"
                  style={{ backgroundColor: '#EF4444' }}
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <input
              type="text"
              name="name"
              value={profileData.name}
              onChange={handleInputChange}
              className="w-full p-2 rounded-xl text-center text-white bg-white/20 placeholder:text-white/60 focus:outline-none"
              placeholder="Full Name"
            />
            
            {/* ✅ Updated Phone Input - Only Numbers */}
            <div className="relative">
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleInputChange}
                onKeyDown={handlePhoneKeyDown}
                onPaste={handlePhonePaste}
                className={`w-full p-2 rounded-xl text-center text-white bg-white/20 placeholder:text-white/60 focus:outline-none ${
                  phoneError ? 'border-2 border-red-400' : ''
                }`}
                placeholder="Phone Number (10 digits)"
                maxLength={10}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {phoneError && (
                <p className="text-xs text-red-300 mt-1 text-center">{phoneError}</p>
              )}
            </div>
            
            <textarea
              name="address"
              value={profileData.address}
              onChange={handleInputChange}
              rows="2"
              className="w-full p-2 rounded-xl text-center text-white bg-white/20 placeholder:text-white/60 focus:outline-none resize-none"
              placeholder="Delivery Address"
            />
            <div className="flex gap-3 justify-center mt-4">
              <button
                onClick={handleUpdateProfile}
                disabled={uploading || !!phoneError}
                className="px-6 py-2 rounded-xl text-white font-medium transition-all hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: phoneError ? '#94A3B8' : '#22C55E' }}
              >
                {uploading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {uploading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelEdit}
                className="px-6 py-2 rounded-xl text-white font-medium transition-all hover:scale-105 flex items-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
              >
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-white">{displayName}</h2>
            {displayPhone && (
              <p className="text-white/80 text-sm flex items-center justify-center gap-1">
                <FaPhone className="text-xs" /> {displayPhone}
              </p>
            )}
            {displayAddress ? (
              <p className="text-white/70 text-sm flex items-center justify-center gap-1">
                📍 {displayAddress}
              </p>
            ) : (
              <p className="text-white/60 text-xs flex items-center justify-center gap-1">
                <FaEdit className="text-[10px]" /> 
                <button onClick={() => setIsEditing(true)} style={{ color: '#FFFFFF' }}>
                  Add your address
                </button>
              </p>
            )}
          </>
        )}
      </div>

      {/* ✅ Settings Section - NO Recent Orders Section */}
      <div className="space-y-1 mb-6">
        {settings.map((item, index) => (
          <Link to={item.path} key={index}>
            <div 
              className="flex items-center justify-between p-3 rounded-xl transition-all hover:bg-opacity-10 cursor-pointer"
              style={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`
              }}
            >
              <div className="flex items-center gap-3">
                <span style={{ color: colors.primary }}>{item.icon}</span>
                <span style={{ color: colors.text }}>{item.label}</span>
              </div>
              <FaChevronRight className="text-xs" style={{ color: colors.textSecondary }} />
            </div>
          </Link>
        ))}
      </div>

      {/* Download App Section */}
      <div 
        className="rounded-2xl p-4 mb-6 text-center"
        style={{ 
          backgroundColor: colors.card,
          border: `1px solid ${colors.border}`
        }}
      >
        <p className="text-sm font-medium" style={{ color: colors.text }}>
          Simple way to get groceries at your doorstep
        </p>
        <button className="mt-2 px-6 py-2 rounded-xl text-white text-sm font-medium flex items-center justify-center gap-2 mx-auto transition-all hover:scale-105" style={{ backgroundColor: colors.primary }}>
          <FaDownload /> Download the app
        </button>
      </div>

      {/* Delete Account */}
      <Link to="/delete-account">
        <div 
          className="flex items-center gap-3 p-3 rounded-xl transition-all hover:scale-[1.02] cursor-pointer mb-2"
          style={{ 
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
          }}
        >
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
            <FaTrash className="text-sm text-red-500" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-sm text-red-600">Delete Account</p>
            <p className="text-xs text-red-500">Permanently delete your account</p>
          </div>
          <FaExclamationTriangle className="text-red-400 text-xs" />
        </div>
      </Link>

      {/* Logout Button */}
      <button
        onClick={handleLogout}
        className="w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
        style={{ 
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          border: '1px solid #FECACA'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#DC2626';
          e.currentTarget.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#FEE2E2';
          e.currentTarget.style.color = '#DC2626';
        }}
      >
        <FaSignOutAlt /> Log Out
      </button>

      <p className="text-center text-xs mt-4" style={{ color: colors.textSecondary }}>App Version 1.0.0</p>
    </motion.div>
  );
};

export default Profile;